import { createContext, useContext } from "react";
import { createStore, StateCreator, StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";

type StoreDefs = Record<string, StateCreator<any>>;

type StoreApiMap<T extends StoreDefs> = {
  [K in keyof T]: StoreApi<ReturnType<T[K]>>;
};

type StoreState<T> = T extends StoreApi<infer S> ? S : never;

export function createStoreModule<T extends StoreDefs>(defs: T) {
  type Stores = StoreApiMap<T>;

  const StoreContext = createContext<Stores | null>(null);

  function createStores(): Stores {
    const stores = {} as Stores;
    for (const key in defs) {
      stores[key] = createStore(defs[key]);
    }
    return stores;
  }

  // 🔥 auto-generated hooks
  const hooks = {} as {
    [K in keyof Stores as `use${Capitalize<string & K>}`]: {
      (): StoreState<Stores[K]>;
      <U>(selector: (state: StoreState<Stores[K]>) => U): U;
    };
  };

  for (const key in defs) {
    const hookName = `use${key.charAt(0).toUpperCase()}${key.slice(1)}`;

    (hooks as any)[hookName] = (selector?: any) => {
      const stores = useContext(StoreContext);

      if (!stores) {
        throw new Error(
          `[StoreModule] Missing provider for store "${key}"`
        );
      }

      return useStore(stores[key], selector ?? ((s: any) => s));
    };
  }

  return {
    StoreContext,
    createStores,
    hooks,
  };
}
